import React, { useState, useEffect } from 'react';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import { useDepartments } from '../../hooks/useDepartments';

export default function UnitModal({
    isOpen,
    onClose,
    onSave,
    editData,
    title = 'Add Unit',
    selectedOrgId,
    isLoading
}) {
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        department: '',
        manager_employee: null
    });

    // Fetch departments for selected organization
    const { data: deptData, isLoading: deptLoading } = useDepartments(selectedOrgId);
    const departments = deptData?.results || deptData || [];

    useEffect(() => {
        if (editData) {
            setFormData({
                name: editData.name || '',
                code: editData.code || '',
                description: editData.description || '',
                department: editData.department_id || editData.department?.id || editData.department || '',
                manager_employee: editData.manager_employee_id || editData.manager_employee?.id || editData.manager_employee || null,
            });
        } else {
            setFormData({
                name: '',
                code: '',
                description: '',
                department: '',
                manager_employee: null,
            });
        }
    }, [editData, isOpen]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Auto-generate code from name (e.g., "IT Support" -> "IT_SUPPORT")
    const handleNameChange = (value) => {
        setFormData(prev => ({
            ...prev,
            name: value,
            // Only auto-update code if user hasn't manually edited it (simplification: just auto-update, user can edit code after name)
            code: value.toUpperCase().replace(/[^A-Z0-9\s]/g, '').replace(/\s+/g, '_'),
        }));
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed p-15 w-full max-w-[500px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1001]">
                <div className="py-10 md:px-20 px-15 bg-bg-dark rounded-lg shadow-xl border border-border">
                    {/* Header */}
                    <div className="flex items-center justify-between py-10 mb-15 border-b border-border">
                        <h5 className="text-[16px] font-medium">{title}</h5>
                        <button onClick={onClose} className="p-5 hover:bg-bg-light rounded-full">
                            <IconX size={18} />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="space-y-15">
                            {/* Department */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">
                                    Department <span className="text-danger">*</span>
                                </label>
                                <select
                                    value={formData.department}
                                    onChange={(e) => handleChange('department', e.target.value)}
                                    required
                                    disabled={deptLoading}
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary"
                                >
                                    <option value="">{deptLoading ? 'Loading...' : 'Select Department'}</option>
                                    {departments.map(dept => (
                                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Name */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">
                                    Unit Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleNameChange(e.target.value)}
                                    placeholder="e.g., IT Support"
                                    required
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary"
                                />
                            </div>

                            {/* Code */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">
                                    Code <span className="text-text-secondary">(Auto-generated)</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                                    placeholder="Auto-generated from name"
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary uppercase"
                                />
                            </div>

                            {/* Description */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Brief description of the unit"
                                    rows={2}
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary resize-none"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-10 justify-end mt-20 pt-15 border-t border-border">
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn btn-outline-secondary btn-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn btn-primary btn-sm flex items-center gap-5"
                            >
                                {isLoading && <IconLoader2 size={16} className="animate-spin" />}
                                {editData ? 'Update' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {/* Backdrop */}
            <div onClick={onClose} className="fixed z-[1000] w-full h-full left-0 top-0 bg-black/50 backdrop-blur-[2px]" />
        </>
    );
}
